import React, { createRef, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useDispatch } from 'react-redux';

// Shared UI Library Components
import { CustomButton } from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './pay-per-session-tab.scss';

// Components
import { AddSessionModal } from '../../../../modals/add-session-modal/add-session-modal';
import { DeleteSessionModal } from '../../../../modals/delete-session-modal/delete-session-modal';
import { SessionItem } from '../session-item/session-item';
import { BookingCancellationTime, TBookingCancellationTimeRef } from "./booking-cancellation-time/booking-cancellation-time";
import {
  ConfirmationBookingAppointment,
  TConfirmationBookingAppointmentRef
} from "./confirmation-booking-appointment/confirmation-booking-appointment";

// Store entities
import {
  getSessionAvailableAttributes,
  getStoreSessionAttributes,
  getAppointmentsSettings,
} from '../../../../store/session-settings/session-settings.thunks';

// Models
import { TSessionType } from '../../../../types/settings.types';
import {
  IAppointmentSettings,
  IStoreSession,
  IStoreSessionAttributes
} from '../../../../interfaces/session-settings.interfaces';

export type TPayPerSessionTabRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TPayPerSessionTabProps = {
  sessions: IStoreSessionAttributes;
  appointmentsSettings: IAppointmentSettings | null;
};

export const PayPerSessionTab = forwardRef<TPayPerSessionTabRef, TPayPerSessionTabProps>((
  { sessions, appointmentsSettings = null }: TPayPerSessionTabProps, ref) => {
  const confirmationBookingAppointmentRef = createRef<TConfirmationBookingAppointmentRef>();
  const bookingCancellationTimeRef = createRef<TBookingCancellationTimeRef>();

  const [deleteSessionOpen, setDeleteSessionOpen] = useState<boolean>(false);
  const [addSessionOpen, setAddSessionOpen] = useState<boolean>(false);
  const [activeSession, setActiveSession] = useState<IStoreSession | null>(null);
  const [sessionType, setSessionType] = useState<TSessionType | null>(null);

  const dispatch = useDispatch<any>();

  const videoSortedSessions = [...sessions?.video_call_session]?.sort((a, b) => (+a?.call_duration - +b?.call_duration));
  const audioSortedSessions = [...sessions?.audio_call_session]?.sort((a, b) => (+a?.call_duration - +b?.call_duration));
  const textSortedSessions = [...sessions?.conversation_session]?.sort((a, b) => (+a?.call_duration - +b?.call_duration));

  const toggleAddSessionModal = (): void => {
    if (addSessionOpen) {
      setActiveSession(null);
    }
    setAddSessionOpen(!addSessionOpen);
  }

  const toggleDeleteSessionModal = (): void => {
    if (deleteSessionOpen) {
      setActiveSession(null);
    }
    setDeleteSessionOpen(!deleteSessionOpen);
  }

  const openAddSessionModal = (type: TSessionType): void => {
    setActiveSession(null);
    setSessionType(type);
    toggleAddSessionModal();
  }

  const openEditSessionModal = (session: IStoreSession, type: TSessionType): void => {
    setActiveSession(session);
    setSessionType(type);
    toggleAddSessionModal();
  }

  const openDeleteSessionModal = (session: IStoreSession): void => {
    setActiveSession(session);
    toggleDeleteSessionModal();
  }

  const loadAppointmentsSettings = (): void => {
    dispatch(getAppointmentsSettings());
  }

  const loadSessionAvailableAttributes = (): void => {
    dispatch(getSessionAvailableAttributes());
  }

  const loadStoreSessionAttributes = (): void => {
    dispatch(getStoreSessionAttributes());
  }

  useImperativeHandle(ref, () => ({
    isHasChanges(): boolean {
      return !!confirmationBookingAppointmentRef.current?.isHasChanges();
    },
    resetState(): void {
      confirmationBookingAppointmentRef.current?.resetState();
    }
  }));

  useEffect(() => {
    loadSessionAvailableAttributes();
    loadStoreSessionAttributes();
    loadAppointmentsSettings();
  }, []);

  return (
    <div className="pay-per-session-tab">
      <AddSessionModal open={ addSessionOpen }
                       session_type={ sessionType }
                       initialData={ activeSession }
                       onCancel={ toggleAddSessionModal }
                       onSubmit={ toggleAddSessionModal }/>
      <DeleteSessionModal open={ deleteSessionOpen }
                          session_type={ activeSession?.session_type }
                          sessionId={ activeSession?.id }
                          onCancel={ toggleDeleteSessionModal }/>

      <div className="pay-per-session-tab--description business--description">
        Settings for booking appointments that are charged according to a predefined duration. This duration is fixed
        and cannot be extended after the call ends. Each appointment automatically ends when the allotted time expires,
        and both the Consultant and the Client are notified 5 minutes before the end.
      </div>
      <div className="pay-per-session-tab--description business--description">
        In addition, it is possible to set the time during which the Consultant must accept or decline the appointment.
        If the Consultant does not respond to the appointment booking within the specified time, this appointment will
        be automatically rejected.
      </div>
      <div className="pay-per-session-tab--appointments">
        { appointmentsSettings && <ConfirmationBookingAppointment ref={ confirmationBookingAppointmentRef } appointmentsSettings={ appointmentsSettings }/> }
        { appointmentsSettings && <BookingCancellationTime ref={ bookingCancellationTimeRef } appointmentsSettings={ appointmentsSettings }/> }
      </div>
      <div className="pay-per-session-tab--body business--body-items">
        <div className="body-item">
          <div className="body-item--title">
            Video Consultation <CustomButton
            text={ 'Add Session' }
            size={ 'small' }
            type={ 'text' }
            onClick={ () => openAddSessionModal('video_call') }
          />
          </div>
          { !sessions.video_call_session.length && <div className="body-item--empty">
            No Sessions yet.
          </div> }
          <div className="body-item--list">
            { videoSortedSessions.map((session) => <SessionItem key={ session.id }
                                                                session={ session }
                                                                onEdit={ openEditSessionModal }
                                                                onDelete={ openDeleteSessionModal }/>) }
          </div>
        </div>

        <div className="body-item">
          <div className="body-item--title">
            Voice Consultation <CustomButton
            text={ 'Add Session' }
            size={ 'small' }
            type={ 'text' }
            onClick={ () => openAddSessionModal('audio_call') }
          />
          </div>
          { !sessions.audio_call_session.length && <div className="body-item--empty">
            No Sessions yet.
          </div> }
          <div className="body-item--list">
            { audioSortedSessions.map((session) => <SessionItem key={ session.id }
                                                                session={ session }
                                                                onEdit={ openEditSessionModal }
                                                                onDelete={ openDeleteSessionModal }/>) }
          </div>
        </div>

        <div className="body-item">
          <div className="body-item--title">
            Text Consultation <CustomButton
            text={ 'Add Session' }
            size={ 'small' }
            type={ 'text' }
            onClick={ () => openAddSessionModal('conversation') }
          />
          </div>
          { !sessions.conversation_session.length && <div className="body-item--empty">
            No Sessions yet.
          </div> }
          <div className="body-item--list">
            { textSortedSessions.map((session) => <SessionItem key={ session.id }
                                                                session={ session }
                                                                onEdit={ openEditSessionModal }
                                                                onDelete={ openDeleteSessionModal }/>) }
          </div>
        </div>
      </div>
    </div>
  );
});
